import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ACTION_STATUSES } from 'shared/consts';
import { BookingStatus } from 'store/bookings/types';
import { selectCart } from 'store/cart/selectors';
import { getTransaction } from 'store/transactions/actions';
import { selectTransaction, selectTransactionStatus } from 'store/transactions/selectors';
import { TransactionDetails } from 'store/transactions/types';

interface CompletedBooking {
  /* The booking just made: the cart's transaction, so a multi-day purchase is its FIRST booking. */
  uuid?: string;
  /*
   * The booking to decide on, once it can be trusted. Undefined while a fresh read is in flight, so the
   * add-on decision cannot latch a "no" off a stale copy.
   */
  transaction?: TransactionDetails;
}

/**
 * RES-1407 — the booking a finished checkout just made, as `BookingCompleted` decides on it.
 *
 * ⛔ THE UUID COMES FROM THE CART, NEVER FROM THE SHARED SLOT ALONE. `state.transactions.transaction` is
 * app-wide: a booking opened earlier in the session sits in it too, so trusting it without the cart's
 * uuid could select someone's previous booking. With no uuid there is nothing to trust, and the
 * landing falls back to the upcoming tab.
 *
 * ⛔ A 3DS BOOKING IS RE-READ. Its checkout fetched the transaction while the charge was still pending
 * (`Pending_Payment`), and the add-on eligibility refuses that status, so deciding on the stored copy
 * would never offer add-ons for a card that needed a challenge. One read, then whatever it returns.
 */
export const useCompletedBooking = (): CompletedBooking => {
  const dispatch = useDispatch();
  const cart = useSelector(selectCart);
  const stored = useSelector(selectTransaction);
  const transactionStatus = useSelector(selectTransactionStatus);

  const uuid = cart?.transaction_uuid ?? undefined;
  const matching = uuid && stored?.uuid === uuid ? stored : undefined;
  const isStale = !matching || matching.details?.status === BookingStatus.PENDING_PAYMENT;

  const fetchedFor = useRef<string | null>(null);
  const needsRead = Boolean(uuid) && isStale && fetchedFor.current !== uuid;

  useEffect(() => {
    if (needsRead && uuid) {
      fetchedFor.current = uuid;
      dispatch(getTransaction.request(uuid));
    }
  }, [dispatch, needsRead, uuid]);

  const isReading = Boolean(uuid) && fetchedFor.current === uuid && transactionStatus === ACTION_STATUSES.PENDING;

  return { uuid, transaction: needsRead || isReading ? undefined : matching };
};
